import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  CalendarDays, Search, Loader2, ChevronRight,
  CheckCircle2, XCircle, Clock, ArrowLeft,
  Users, MapPin, Calendar, Check, Pause,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { eventsApi, type EventResponse } from "@/lib/api";
import { toast } from "sonner";
import { resolveMediaUrl } from "@/lib/api";

export const Route = createFileRoute("/admin/events")({
  head: () => ({
    meta: [{ title: "Events — OyoBooking" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: AdminEventsPage,
});

const STATUS_STYLES: Record<string, string> = {
  upcoming: "bg-blue-500/10 text-blue-600",
  active: "bg-green-500/10 text-green-600",
  suspended: "bg-amber-500/10 text-amber-600",
  cancelled: "bg-red-500/10 text-red-600",
  completed: "bg-slate-500/10 text-slate-600",
};

function getStatusStyle(status: string) {
  return STATUS_STYLES[status] ?? "bg-muted text-muted-foreground";
}

function AdminEventsPage() {
  const { user, isBusinessOwner } = useAuth();
  const [events, setEvents] = useState<EventResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const isAdmin = !isBusinessOwner;

  useEffect(() => {
    loadEvents();
  }, [user?.id]);

  async function loadEvents() {
    setLoading(true);
    try {
      const params: { pageSize: number; managerId?: string } = { pageSize: 200 };
      if (isBusinessOwner && user?.id) {
        params.managerId = user.id;
      }
      const res = await eventsApi.list(params);
      setEvents(res.data);
    } catch {
      toast.error("Failed to load events");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }

  async function updateStatus(id: string, status: string) {
    setUpdatingId(id);
    try {
      await eventsApi.update(id, { status });
      toast.success(`Event ${status}`);
      setEvents(prev => prev.map(e => e.id === id ? { ...e, status } : e));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Update failed";
      toast.error(msg);
    } finally {
      setUpdatingId(null);
    }
  }

  const filtered = events.filter(e => {
    const q = search.toLowerCase();
    return e.name.toLowerCase().includes(q) || e.eventRef.toLowerCase().includes(q) || e.organizer.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link to="/admin/dashboard" className="text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="size-4" />
            </Link>
            <h1 className="font-display text-xl font-bold">{isAdmin ? "All Events" : "My Events"}</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            {isAdmin ? "Monitor and manage platform events." : "Manage your events and ticket sales."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search events…"
              className="w-56 rounded-full pl-9 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
        <StatCard label="Total" value={events.length} />
        <StatCard label="Upcoming" value={events.filter(e => e.status === "upcoming").length} color="blue" />
        <StatCard label="Active" value={events.filter(e => e.status === "active").length} color="green" />
        <StatCard label="Suspended" value={events.filter(e => e.status === "suspended").length} color="amber" />
        <StatCard label="Cancelled" value={events.filter(e => e.status === "cancelled").length} color="red" />
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-border/60 bg-card shadow-soft overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading events…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CalendarDays className="mb-2 size-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No events found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  <th className="px-4 py-3 sm:px-5">Event</th>
                  <th className="px-4 py-3 sm:px-5">Organizer</th>
                  <th className="px-4 py-3 sm:px-5">Venue</th>
                  <th className="px-4 py-3 sm:px-5">Date</th>
                  <th className="px-4 py-3 sm:px-5">Status</th>
                  <th className="px-4 py-3 sm:px-5">Registrations</th>
                  <th className="px-4 py-3 sm:px-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {filtered.map(ev => (
                  <tr key={ev.id} className="hover:bg-accent/20 transition-colors">
                    <td className="px-4 py-3 sm:px-5">
                      <div className="flex items-center gap-3">
                        {ev.image ? (
                          <img src={resolveMediaUrl(ev.image)} alt="" className="size-10 rounded-lg object-cover shrink-0" />
                        ) : (
                          <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                            <CalendarDays className="size-4 text-muted-foreground" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium truncate">{ev.name}</p>
                          <p className="text-xs text-muted-foreground font-mono">{ev.eventRef}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 sm:px-5 text-muted-foreground">{ev.organizer}</td>
                    <td className="px-4 py-3 sm:px-5 text-muted-foreground">{ev.venueName}</td>
                    <td className="px-4 py-3 sm:px-5">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Calendar className="size-3.5" />
                        {new Date(ev.date).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}
                      </div>
                    </td>
                    <td className="px-4 py-3 sm:px-5">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${getStatusStyle(ev.status)}`}>
                        {ev.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 sm:px-5">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Users className="size-3.5" />
                        {ev.totalRegistrations}
                      </div>
                    </td>
                    <td className="px-4 py-3 sm:px-5">
                      <div className="flex items-center justify-end gap-1.5">
                        {isBusinessOwner && ev.status !== "cancelled" && ev.status !== "suspended" && (
                          <Link to="/events/$eventId" params={{ eventId: ev.id }} search={{ edit: true }}>
                            <Button variant="ghost" size="sm" className="h-8 rounded-lg text-xs">Edit</Button>
                          </Link>
                        )}
                        {isAdmin && ev.status !== "active" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 rounded-lg text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => updateStatus(ev.id, "active")}
                            disabled={updatingId === ev.id}
                          >
                            {updatingId === ev.id ? <Loader2 className="size-3 animate-spin" /> : <Check className="size-3" />}
                            Approve
                          </Button>
                        )}
                        {isAdmin && ev.status === "active" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 rounded-lg text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            onClick={() => updateStatus(ev.id, "suspended")}
                            disabled={updatingId === ev.id}
                          >
                            {updatingId === ev.id ? <Loader2 className="size-3 animate-spin" /> : <Pause className="size-3" />}
                            Suspend
                          </Button>
                        )}
                        {isAdmin && ev.status !== "cancelled" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 rounded-lg text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => updateStatus(ev.id, "cancelled")}
                            disabled={updatingId === ev.id}
                          >
                            {updatingId === ev.id ? <Loader2 className="size-3 animate-spin" /> : <XCircle className="size-3" />}
                            Cancel
                          </Button>
                        )}
                        <Link to={`/events/${ev.id}`}>
                          <Button variant="ghost" size="sm" className="h-8 rounded-lg text-xs">
                            View <ChevronRight className="size-3 ml-0.5" />
                          </Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color?: string }) {
  const colorMap: Record<string, string> = {
    blue: "text-blue-600",
    green: "text-green-600",
    amber: "text-amber-600",
    red: "text-red-600",
  };
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-4 shadow-soft">
      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{label}</p>
      <p className={`mt-1 text-2xl font-bold font-[family-name:Inter] ${color ? colorMap[color] : "text-foreground"}`}>{value}</p>
    </div>
  );
}
